// Internal modules
import { appStore, dispatchFormAction } from '@/store';
import { logger } from '@/lib';
import { CRS_TAX_DETAILS_FIELDS, CRS_TAXPAYERID_OPTIONS } from './constants';
import { updateStateIfEmpty, updateTaxPayerIdValueIfInvalid } from './utils';
import { getCrsFields } from '../../store/models/utils';
import { getLocale } from '../../lib/globals';
import { CRS_NO_TIN_COUNTRIES } from '../../lib/constants';

/**
 * @description: Adds a new tax residency to the CRS tax details list
 * @param {Object} crsTaxDetails crs tax details
 * @returns {void}
 */
const addNewTaxResidencyChangeHandler = ({ crsTaxDetails }) => {
  /// Check for validation errors before adding new tax residency
  const isTaxFieldsValid = validateCRSFormFields();
  if (isTaxFieldsValid) {
    logger.info('crs_form_fields_validation_success');
    const updatedState = [...crsTaxDetails];
    updatedState.push(getCrsFields());
    dispatchFormAction({
      field: 'crsTaxDetails',
      payload: { value: updatedState },
    });
    logger.fpti('crs_add_country_and_tax_details', {
      onboard_country: getLocale().country,
      state_name: 'crs_section',
      total_tax_residencies: updatedState.length,
      transition_name: 'process_add_country_click',
    });
    logger.info('added_new_tax_residency');
  }
};

/**
 * @description: Validates CRS Tax Details fields
 * @returns {Boolean} isTaxDetailsValid
 */

const validateCRSFormFields = () => {
  const { COUNTRY_CODE, TAX_PAYER_ID_VALUE, NO_TAX_PAYER_ID_REASON } =
    CRS_TAX_DETAILS_FIELDS;
  const { form: formState } = appStore.getState();
  const crsTaxDetails = formState.crsTaxDetails.value;
  const onboardingCountry = getLocale().country;
  let isTaxDetailsValid = true;
  const updatedState = [];

  /// Check if any existing TIN fields have backend validation error messages
  const hasExistingErrors = crsTaxDetails.some(
    taxDetail => taxDetail.taxPayerIdValue.displayError,
  );

  if (hasExistingErrors) {
    logger.info('weasley_user_attempted_submit_with_tin_server_errors');
    logger.fpti('weasley_submission_blocked_by_tin_server_errors', {
      int_error_code: '3',
      int_error_desc: 'user_attempted_submit_with_tin_server_errors',
      onboard_country: onboardingCountry,
      state_name: 'crs_section',
      transition_name: 'process_valid_tax_details',
    });
    return false;
  }

  /// Get the buyer's country
  /**
   * This block here validates CRS Tax Details: Country,
   * Taxpayer ID value, Reason for not having a tax id
   */
  crsTaxDetails.forEach((taxCountryDetail, index) => {
    const { taxPayerIdType } = taxCountryDetail;
    let { countryCode, taxPayerIdValue, noTaxPayerIdReason } = taxCountryDetail;

    const shouldSkipTaxIdValidation =
      CRS_NO_TIN_COUNTRIES.includes(countryCode.value) &&
      onboardingCountry === countryCode.value;

    // Check if country code was selected
    [countryCode, isTaxDetailsValid] = updateStateIfEmpty({
      content: 'crs.errors.taxResidencyCountryRequired',
      fieldNameForLogging: `${COUNTRY_CODE}_${index}`,
      fieldValues: countryCode,
      isTaxDetailsValid,
    });

    if (
      taxPayerIdType !== CRS_TAXPAYERID_OPTIONS.NO_TAXPAYER_ID &&
      !shouldSkipTaxIdValidation
    ) {
      const taxPayerIdValuelog = `${TAX_PAYER_ID_VALUE}_${index}`;

      // Check if taxpayer id value was added
      [taxPayerIdValue, isTaxDetailsValid] = updateStateIfEmpty({
        content: 'crs.errors.tinRequired',
        fieldNameForLogging: taxPayerIdValuelog,
        fieldValues: taxPayerIdValue,
        isTaxDetailsValid,
      });
      // Check if taxpayer id value is valid according to the country
      [taxPayerIdValue, isTaxDetailsValid] = updateTaxPayerIdValueIfInvalid({
        countryCode,
        fieldNameForLogging: taxPayerIdValuelog,
        isTaxDetailsValid,
        taxPayerIdValue,
      });
    }
    // In case user has no TIN, we need to check if reason was provided
    else if (!shouldSkipTaxIdValidation) {
      [noTaxPayerIdReason, isTaxDetailsValid] = updateStateIfEmpty({
        content: 'crs.errors.reason',
        fieldNameForLogging: `${NO_TAX_PAYER_ID_REASON}_${index}`,
        fieldValues: noTaxPayerIdReason,
        isTaxDetailsValid,
      });
    }

    updatedState.push({
      ...taxCountryDetail,
      countryCode,
      noTaxPayerIdReason,
      taxPayerIdType,
      taxPayerIdValue,
    });
  });

  if (!isTaxDetailsValid) {
    dispatchFormAction({
      field: 'crsTaxDetails',
      payload: { value: updatedState },
    });
    logger.info('crs_form_fields_validation_failed');
  }

  /// In case, all tax details are valid, log FPTI event for success tax details
  if (isTaxDetailsValid) {
    logger.fpti('crs_valid_tax_details', {
      onboard_country: onboardingCountry,
      state_name: 'crs_section',
      transition_name: 'process_valid_tax_details',
      valid_tax_residencies: updatedState.length,
    });
    logger.info('crs_form_fields_validation_success');
  }

  return isTaxDetailsValid;
};

export { addNewTaxResidencyChangeHandler, validateCRSFormFields };
